import React, { useState, useEffect } from 'react';
import { Alert, Button, Text, Input } from '@perch-labs/shrike';
import { copyFor } from 'config';
import { Form } from 'components/form';
import { translateError } from 'utils/errors/translate-error';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { useUser } from 'hooks/use-user.hook';
import { StyledSelect } from '../screens.style';
import { useUtilitiesByZip } from './use-utilities-by-zip.hook';

const ZIP_CODE_LENGTH = 5;
const getCopy = copyFor('settings.moving.addAccountDetails');

const toSelectOption = utility => ({
  text: utility.displayName,
  value: utility.id,
});

type Props = {
  context: MovingStateMachineContext;
  matches: (state: string) => boolean;
  next: (...args: any[]) => any;
};

export const AddMovingDetails = ({ matches, next, context }: Props) => {
  const [zipCode, setZipCode] = useState(context.zipCode);
  const [utility, setUtility] = useState(context.utility);
  const [nickname, setNickname] = useState(context.nickname);

  // Reset fields on mount (to override any stale context values)
  useEffect(() => {
    setZipCode('');
    setUtility(undefined);
    setNickname('');
  }, []);

  const validZipCodeLength = zipCode.length === ZIP_CODE_LENGTH;
  const { data, loading, error } = useUtilitiesByZip(
    zipCode,
    validZipCodeLength
  );

  const flags = useFlags();
  const ldClient = useLDClient();
  const user = useUser();
  const funnel = user.data?.originationSource?.funnel;
  const custom = {
    funnel: funnel ?? 'unknown',
    utilityId: utility?.id,
  };
  useEffect(() => {
    ldClient?.identify({
      key: 'user_key',
      custom,
    });
  }, [custom]);

  const enrollmentOptions = flags.ffUtilityEnrolmentOptions;
  const { suggested } = data || {};
  const utilitySuggestedOptions = suggested?.map(toSelectOption);

  const handleUtility = utilityId => {
    const selectedUtility = [...suggested].find(
      utility => utility.id === Number(utilityId)
    );
    setUtility(selectedUtility);
  };

  useEffect(() => {
    if (!validZipCodeLength && utility) {
      setUtility(undefined);
    }
  }, [validZipCodeLength, utility, setUtility]);

  useEffect(() => {
    const firstOption = utilitySuggestedOptions?.[0];
    if (firstOption && !utility) {
      handleUtility(firstOption.value);
    }
  }, [data, setUtility, utility]);

  const handleSumbit = () => {
    if (utility.vnmEligible && !context.selectedPlan) {
      localStorage.setItem('nonVNMtoVNMMoveRequest', 'true');
    }
    return next({ nickname, utility, zipCode, enrollmentOptions });
  };

  return (
    <Form onSubmit={handleSumbit}>
      <Text textStyle="heading800" tag="h1" margin={{ bottom: '16px' }}>
        {getCopy('title')}
      </Text>
      <Text>{getCopy('description')}</Text>
      <Input
        className="notranslate"
        errorText={translateError(error)}
        label={getCopy('zipCodeInput.label')}
        margin={{ bottom: '24px', top: '48px' }}
        maxLength={ZIP_CODE_LENGTH}
        name={getCopy('zipCodeInput.label')}
        onChange={setZipCode}
        value={zipCode}
        placeholder={getCopy('zipCodeInput.placeholder')}
      />
      <StyledSelect
        className="notranslate"
        disabled={!utilitySuggestedOptions?.length}
        options={utilitySuggestedOptions}
        label={getCopy('utilityDropdown.label')}
        loading={loading}
        name={getCopy('utilityDropdown.label')}
        onChange={handleUtility}
        value={utility?.id}
        withEmptyPlaceholder={true}
      />
      <Input
        className="notranslate"
        label={getCopy('nicknameInput.label')}
        margin={{ bottom: '48px' }}
        name={getCopy('nicknameInput.label')}
        placeholder={getCopy('nicknameInput.placeholder')}
        onChange={setNickname}
        value={nickname}
        helperText={getCopy('nicknameInput.helperText')}
      />
      <Alert className="notranslate" margin={{ bottom: '16px' }}>
        {translateError(context.errors.createOrUpdateMoveRequest)}
      </Alert>
      <Button
        fullWidth
        disabled={!utility?.id}
        loading={matches('addMovingDetails.processMoveRequest')}
        type="submit"
      >
        {getCopy('nextButton')}
      </Button>
    </Form>
  );
};
